import type { EvChargingNarrativeSnapshot } from './evChargingNarrative';
import { compareToReference, formatComparatorPhrase, isFiniteNumber } from './narrativeFormatting';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';

export function buildEvChargingNarrativeTokens(s: EvChargingNarrativeSnapshot): NarrativeTokenMap {
  const year = s.snapshotYear ? String(s.snapshotYear) : '';
  const snapshotDate = s.snapshotYear ? String(s.snapshotYear) : 'latest';

  if (s.viewId === 'counts') {
    const benchmarkSentence =
      s.target2030 != null && s.target2030 > 0
        ? ` An illustrative population-scaled California 2030 infrastructure benchmark (not an adopted local target) would be about ${Math.round(s.target2030).toLocaleString()} public EVSE ports.`
        : '';
    return {
      locationName: s.locationName,
      snapshotDate,
      totalPorts: s.totalChargers,
      level2Ports: s.l2Chargers,
      dcFastPorts: s.dcFastChargers,
      benchmarkSentence,
    };
  }

  if (s.viewId === 'peoplePerCharger' && s.peoplePerCharger) {
    const { city, county, state } = s.peoplePerCharger;
    const cmp = compareToReference(city, state ?? null);
    const stateSentence =
      isFiniteNumber(state) && cmp.relation !== 'unavailable'
        ? ` ${formatComparatorPhrase(cmp.relation, 'California')} California’s ${Math.round(state).toLocaleString()} people per port.`
        : ' A comparable statewide people-per-port benchmark is unavailable for this snapshot.';
    const countySentence =
      county != null
        ? ` ${s.countyName} County had about ${Math.round(county).toLocaleString()} people per port.`
        : '';
    return {
      locationName: s.locationName,
      snapshotYear: year,
      cityPeoplePerPort: Math.round(city),
      countySentence,
      stateSentence,
    };
  }

  if (s.viewId === 'evsPerCharger' && s.evsPerCharger) {
    const { city, county, state } = s.evsPerCharger;
    const countySentence =
      county != null
        ? ` ${s.countyName} County: about ${Math.round(county).toLocaleString()} plug-in vehicles per port.`
        : '';
    const stateSentence =
      s.stateFleetAvailable && isFiniteNumber(state)
        ? ` ${formatComparatorPhrase(compareToReference(city, state).relation, 'California')} California’s ${Math.round(state).toLocaleString()} EVs per port.`
        : ' A comparable statewide EVs-per-port value is unavailable for this data vintage.';
    return {
      locationName: s.locationName,
      snapshotYear: year,
      cityEvsPerPort: Math.round(city),
      countySentence,
      stateSentence,
    };
  }

  if (s.viewId === 'map') {
    const med = s.mapTractMedianChargers;
    return {
      locationName: s.locationName,
      tractCount: s.mapTractCount,
      medianPorts: med != null ? Math.round(med) : null,
      zeroPortSentence: '',
      rangeSentence: '',
    };
  }

  return { locationName: s.locationName };
}
